import { useRef, useState } from "react";
import { Share2, Download, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CANVAS_FONT, loadCanvasFont } from "@/lib/canvasFont";
import { drawWordmark } from "@/lib/canvasWordmark";

/**
 * How rest reads on a card someone else will see.
 *
 * Phrased as a fact about the run, never as a deduction from it, and no
 * qualifier that invites the reader to discount the number. The app does not
 * punish rest, so neither does the card.
 *
 * "plus", not "including" (2026-10-13): the streak counts only the days the
 * habit was done (services/habit_service.streak_with_rest) and its rest days
 * sit beside it — "42 · 5 rest" is 42 done + 5 rested. "Including" said the
 * rest was inside the 42, which understated what was done.
 */
export function restLine(restDays: number): string {
  return `plus ${restDays} rest ${restDays === 1 ? "day" : "days"}`;
}

/**
 * The card is a fixed 600×400 with a footer: the name sits at y=340 and the
 * watermark at y=365. Everything above has to fit above them.
 *
 * Habit titles are allowed 200 characters, and the subtitle is the habit title.
 * Wrapped at 16px across 500px that is roughly four lines, which would put the
 * last one — and the rest line under it — straight through the name. So the
 * subtitle is capped and the overflow marked with an ellipsis rather than
 * silently drawn over the footer.
 *
 * Pure, and takes its own measure function, so it can be tested without a
 * canvas. The DOM-less test environment has been the standing excuse for not
 * covering this file; measurement is the only part that actually needs a DOM.
 */
export function wrapLines(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
  maxLines = Infinity,
): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of text.split(" ")) {
    const test = current ? `${current} ${word}` : word;
    if (measure(test) > maxWidth) {
      if (current) lines.push(current);
      current = word;
    } else {
      current = test;
    }
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);

  // Something was dropped — say so, rather than ending mid-thought.
  const consumed = lines.join(" ");
  if (consumed.length < text.length) {
    const last = lines.length - 1;
    if (last >= 0 && !lines[last].endsWith("…")) lines[last] = `${lines[last]}…`;
  }
  return lines;
}

/** Subtitle lines the card has room for above the name and watermark. */
const MAX_SUBTITLE_LINES = 2;

interface ShareableAchievementProps {
  type: "streak" | "levelup" | "badge" | "graduation" | "milestone";
  title: string;
  subtitle?: string;
  value: string | number;
  emoji: string;
  userName?: string;
  /**
   * Rest days inside the run this card is celebrating.
   *
   * This is the most public surface in the app — a PNG that leaves it and gets
   * compared against other people's. The product rule is that rest is
   * unlimited BECAUSE it is visible: a streak that hides its rest days would
   * need a cap to stay meaningful, and one that shows them stays honest on its
   * own. So the number never travels without what it is made of.
   *
   * 0 means the run really was unbroken completions, and nothing is drawn.
   */
  restDays?: number;
}

export default function ShareableAchievement({ type, title, subtitle, value, emoji, userName, restDays = 0 }: ShareableAchievementProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shared, setShared] = useState(false);
  const { toast } = useToast();

  const generateImage = async (): Promise<Blob | null> => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    await loadCanvasFont();

    const w = 600;
    const h = 400;
    canvas.width = w;
    canvas.height = h;

    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, "#1a1612");
    gradient.addColorStop(1, "#0f0d0a");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = "rgba(202, 158, 50, 0.3)";
    ctx.lineWidth = 3;
    ctx.strokeRect(20, 20, w - 40, h - 40);

    const cornerSize = 30;
    ctx.strokeStyle = "#ca9e32";
    ctx.lineWidth = 2;
    [[20, 20, 1, 1], [w - 20, 20, -1, 1], [20, h - 20, 1, -1], [w - 20, h - 20, -1, -1]].forEach(([x, y, dx, dy]) => {
      ctx.beginPath();
      ctx.moveTo(x as number, (y as number) + (dy as number) * cornerSize);
      ctx.lineTo(x as number, y as number);
      ctx.lineTo((x as number) + (dx as number) * cornerSize, y as number);
      ctx.stroke();
    });

    ctx.font = "48px serif";
    ctx.textAlign = "center";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(emoji, w / 2, 120);

    ctx.font = `bold 32px ${CANVAS_FONT}`;
    ctx.fillStyle = "#ca9e32";
    ctx.fillText(String(value), w / 2, 175);

    ctx.font = `bold 22px ${CANVAS_FONT}`;
    ctx.fillStyle = "#ffffff";
    ctx.fillText(title, w / 2, 220);

    let y = 255;
    if (subtitle) {
      ctx.font = `16px ${CANVAS_FONT}`;
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      const lines = wrapText(ctx, subtitle, w - 100, MAX_SUBTITLE_LINES);
      lines.forEach((line) => {
        ctx.fillText(line, w / 2, y);
        y += 22;
      });
    }

    // The rest behind the number, drawn in the same gold as the headline value
    // rather than a dimmer grey: this is not a disclaimer or an asterisk on the
    // achievement, it is part of it.
    // Clamped as well as capped. The cap above keeps the subtitle short, but a
    // belt-and-braces floor means a future change to the layout above can't
    // push this line down through the name at y=340.
    if (restDays > 0) {
      ctx.font = `15px ${CANVAS_FONT}`;
      ctx.fillStyle = "rgba(202, 158, 50, 0.85)";
      ctx.fillText(restLine(restDays), w / 2, Math.min(y + 6, 315));
    }

    if (userName) {
      ctx.font = `14px ${CANVAS_FONT}`;
      ctx.fillStyle = "rgba(202, 158, 50, 0.7)";
      ctx.fillText(userName, w / 2, h - 60);
    }

    // The app's wordmark, white like the header (was "TrybeUP™" in gold text).
    drawWordmark(ctx, w / 2, h - 46, 11, "rgba(255, 255, 255, 0.6)");

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines?: number,
  ): string[] =>
    wrapLines(text, maxWidth, (s) => ctx.measureText(s).width, maxLines);

  const handleShare = async () => {
    const blob = await generateImage();
    if (!blob) return;

    const file = new File([blob], "trybeup-achievement.png", { type: "image/png" });
    // The caption is as public as the image, and on platforms that drop the
    // file it is the ONLY thing that travels. Rest goes in both or neither.
    const shareText = `${emoji} ${title} — ${value}${subtitle ? ` | ${subtitle}` : ""}${
      restDays > 0 ? ` (${restLine(restDays)})` : ""
    }`;

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: "My TrybeUP Achievement",
          text: shareText,
          files: [file],
        });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
        return;
      } catch {
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trybeup-achievement.png";
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded!", description: "Achievement card saved to your device." });
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  return (
    <>
      <canvas ref={canvasRef} className="hidden" />
      <button
        onClick={handleShare}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
        aria-label="Share achievement"
      >
        {shared ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
        {shared ? "Shared!" : "Share"}
      </button>
    </>
  );
}
